import React, { useCallback, useMemo, useState, createContext, useContext, useEffect } from "react";
import { EMenuItems, EMenuItemTypes, EMenuSubItemTypes, TMenuItem, TMenuSubItem } from "../components/menu/menu-items";
import { v4 as uuidv4 } from "uuid";
import {
  TDocumentBase,
  Themes,
  TVirtualWindowData,
  TWindowPosition,
  TWindowSize,
  SearchLeaf,
  OptionType,
  ULTIMELE_APARITII_WINDOW_ID,
  TDocumentRel,
} from "../types";
import { newApiService } from "../services/api-service";
import { UserContext } from "../../context";
import { AdvancedSearchVintageFullModal } from "../components/search/AdvancedSearch";
import { useCache, TCacheData, CACHE_KEYS, CacheKeyValues } from "../contexts/CacheContext";
import { printDiv, printSelection } from "../services/printer";
import { getSelectionHtml, titleForDoc } from "../services/exporter";
import useTextSelectorObserver from "../hooks/useTextSelectorObserver";
import { copyTextToClipboard, selectAllTextInDiv } from "../services/text";
import DocumentRelsModal from "../components/document-viewer/DocumentRelsModal";
import DocumentHistoryModal, { TDocumentHistoryData } from "../components/document-viewer/DocumentHistoryModal";
import InDocumentSearchModal from "../components/document-viewer/InDocumentSearchModal";
import NoSelectionModal from "../components/document-viewer/NoSelectionModal";
import ExportingModal from "../components/document-viewer/ExportingModal";

export enum SideBarTabs {
  INDEX = "index",
  SEARCH = "search-results",
  BOOKMARKS = "bookmarked-docs",
  MONITORS = "monitors",
  READ_LATER = "read-later-docs",
}

export type SearchTerms = {
  type: OptionType;
  terms: string[];
};

export type SearchProps = {
  domains: OptionType[];
  issuers: OptionType[];
  types: OptionType[];

  // we don't have this
  state: OptionType[];

  searchTerms: SearchTerms;
  doc_nr: { start?: number; end?: number; isRange?: boolean };
  doc_year: { start?: number; end?: number; isRange?: boolean };
  mof_nr: { start?: number; end?: number; isRange?: boolean };
  mof_year: { start?: number; end?: number; isRange?: boolean; month?: OptionType };
};

export type TDesktopContext = {
  // docSearchText?: string,
  // setDocSearchText: (text: string | undefined) => void,
  cache: {
    isLoading: boolean;
    cache: Record<CacheKeyValues, TCacheData>;
    set: (key: CacheKeyValues, data: any, expiresInMilliseconds?: number) => void;
    get: (key: CacheKeyValues) => TCacheData;
    invalidate: (key: CacheKeyValues) => void;
    isBookmarked: (id: string) => boolean | undefined;
    isReadLater: (id: string) => boolean | undefined;
  };
  showDocumentControls: boolean;
  showStatusBar: boolean;
  showDocumentControlsText: boolean;
  menuItems: TMenuItem[];
  searchResults: SearchLeaf[];
  isLoading: boolean;
  error: string | null;
  windows: TVirtualWindowData[];
  minimizedWindows: TVirtualWindowData[];
  activeTab: SideBarTabs;
  setActiveTab: (tab: SideBarTabs) => void;
  searchProps: SearchProps;
  setSearchProps: React.Dispatch<React.SetStateAction<SearchProps>>;
  clearSearchProps: () => void;
  onSearch: () => void;
  searchWithFilters: (filters: Record<string, any>) => Promise<void>;
  onInnerDocumentSearch: (text: string, caseSensitive?: boolean, backwards?: boolean) => string | null;
  onNextInnerDocumentSearch: (backwards?: boolean) => string;
  onInnerDocumentSearchResult: (count: number) => void;
  unsetInnerDocumentSearch: () => void;
  openDocument: (doc: TDocumentBase) => void;
  openLatestDocsDocument: () => void;
  updateWindowTitle: (windowId: string, title: string) => void;
  openLink: (windowId: string, docId: string, anchorId?: string) => void;
  focusedWindowPrevious: () => void;
  focusedWindowNext: () => void;
  closeWindow: (windowId: string) => void;
  closeFocusedWindow: () => void;
  minimizeWindow: (windowId: string) => void;
  minimizeFocusedWindow: () => void;
  maximizeWindow: (windowId: string) => void;
  maximizeRestoreFocusedWindow: () => void;
  minimizeVisibleMaximizedWindow: () => void;
  restoreVisibleMaximizedWindow: () => void;
  closeVisibleMaximizedWindow: () => void;
  restoreWindow: (windowId: string) => void;
  bringToFront: (windowId: string) => void;
  updateWindowPosition: (windowId: string, position: TWindowPosition) => void;
  printFocusedWindow: () => void;
  printSelection: () => void;
  toggleShowReds: (windowId: string) => void;
  exportFocusedWindowDocx: () => Promise<void>;
  exportFocusedWindowPDF: () => Promise<void>;
  cascadeWindows: () => void;
  tileWindowsHorizontally: () => void;
  tileWindowsVertically: () => void;
  minimizeAllWindows: () => void;
  closeAllWindows: () => void;
  updateWindowSize: (windowId: string, size: TWindowSize) => void;
  showAdvancedSearch: () => void;
  showInDocumentSearch: () => void;
  showFocusedWindowsDocumentRels: () => Promise<void>;
  showFocusedWindowsDocumentHistory: () => Promise<void>;
  addFocusedWindowToFavorites: () => Promise<boolean | undefined>;
  toggleFocusedWindowFavorites: () => Promise<void>;
  hasFocusedWindow: boolean;
  hasAnyFocusedWindow: boolean;
  isFocusedWindowInFavorites: boolean;
};

export const DesktopContext = createContext<TDesktopContext | undefined>(undefined);

// const generateNewWindowMeta = (): TWindowData => ({
//     id: uuidv4(),
//     state: EWindowState.DEFAULT,
//     position: { x: 0, y: 0 },
//     size: {
//         width: 30 * 16, // 30 rem
//         height: 20 * 16, // 20 rem
//     }
// })

const getNextWindowStartPoint = (windows: TVirtualWindowData[]) => {
  let current = { x: 48, y: 48 };
  while (windows.find((w) => w.position.x === current.x && w.position.y === current.y)) {
    current.x += 16;
    current.y += 16;
  }
  return current;
};

const calculateGridSize = (containers: number) => {
  const cols = Math.floor(Math.sqrt(containers));
  const rows = containers / cols;
};

const emptySearchProps: SearchProps = {
  domains: [],
  issuers: [],
  types: [],

  // we don't have these two
  state: [],

  searchTerms: { type: { value: "titlu", label: "In titlu" }, terms: [] },
  doc_nr: {},
  doc_year: {},
  mof_nr: {},
  mof_year: { },
};

export const DesktopContextWrapper = ({ children }: { children: any }) => {
  // const [menuItems, setMenuItems] = useState<TMenuItem[]>([]);
  const { theme: rawTheme } = useContext(UserContext);
  // const theme = useMemo<Themes | undefined>(() => (loggedInUser as any)?.theme, [loggedInUser]); // TODO - move this to the user context
  const theme = useMemo(() => rawTheme as Themes, [rawTheme]);
  const cache = useCache();

  const selectedText = useTextSelectorObserver();

  const [showAbout, setShowAbout] = useState(false);

  const [activeTab, setActiveTab] = useState<SideBarTabs>(SideBarTabs.INDEX);
  const [showDocumentControls, setShowDocumentControls] = useState(true);
  const [showStatusBar, setShowStatusBar] = useState(true);
  const [showDocumentControlsText, setShowDocumentControlsText] = useState(true);

  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);
  const [loadingDocumentRelsData, setLoadingDocumentRelsData] = useState(false);
  const [showDocumentRelsData, setShowDocumentRelsData] = useState<TDocumentRel[] | undefined>();
  const [loadingDocumentHistoryData, setLoadingDocumentHistoryData] = useState(false);
  const [showDocumentHistoryData, setShowDocumentHistoryData] = useState<TDocumentHistoryData | undefined>();
  const [showInDocumentSearchModal, setShowInDocumentSearchModal] = useState(false);
  const [showNoSelectionModal, setShowNoSelectionModal] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<'docx' | 'pdf' | undefined>();

  const handlePrintSelection = useCallback(() => {
    if (!printSelection()) setShowNoSelectionModal(true);
  }, []);


  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [windows, setWindows] = useState<TVirtualWindowData[]>([]);
  const [minimizedWindows, setMinimizedWindows] = useState<TVirtualWindowData[]>([]);

  const [searchResults, setSearchResults] = useState<SearchLeaf[]>([]);
  const [searchProps, setSearchProps] = useState<SearchProps>(emptySearchProps);

  const hasFocusedWindow = useMemo(() => {
    const focusedWindow = windows.find((w) => w.focused);
    if (!focusedWindow) return false;
    return focusedWindow.id !== ULTIMELE_APARITII_WINDOW_ID;
  }, [windows]);

  const hasAnyFocusedWindow = useMemo(() => {
    const focusedWindow = windows.find((w) => w.focused);
    return !!focusedWindow;
  }, [windows]);

  const onSearch = useCallback(async () => {
    setActiveTab(SideBarTabs.SEARCH);
    const filters: Record<string, any> = {};
    if (searchProps.doc_nr.start) {
      filters.id = searchProps.doc_nr;
    }
    if (searchProps.doc_year.start) {
      filters.year = searchProps.doc_year;
    }
    if (searchProps.mof_nr.start) {
      filters.id_mof = searchProps.mof_nr;
    }
    if (searchProps.mof_year.start) {
      filters.year_mof = searchProps.mof_year;
    }
    if (searchProps.searchTerms.terms.length > 0) {
      filters.text = searchProps.searchTerms.terms.map((t) => ({ type: searchProps.searchTerms.type.value, value: t }));
    }
    if (searchProps.issuers.length > 0) {
      filters.issuers = searchProps.issuers;
    }
    if (searchProps.domains.length > 0) {
      filters.domains = searchProps.domains;
    }
    if (searchProps.types.length > 0) {
      filters.type = searchProps.types;
    }
    if (searchProps.state.length > 0) {
      filters.status = searchProps.state;
    }
    const result = await newApiService.search(filters, 0, 100);
    console.log("search result", { searchProps, result });
    // setSearchResults([]); // TODO: @Adi - check this..; without this, when performing a second search with "Results" tab open, it does not re-render
    setSearchResults(result);
  }, [searchProps]);

  const searchWithFilters = useCallback(async (filters: Record<string, any>) => {
    setActiveTab(SideBarTabs.SEARCH);
    const result = await newApiService.search(filters, 0, 100);
    setSearchResults(result);
  }, []);


  const onNextInnerDocumentSearch = useCallback((backwards?: boolean) => {
    let result = '';
    const relevantWindow = windows.find(w => w.focused && w.innerSearch);
    if (relevantWindow) {
      if (backwards && relevantWindow.innerSearch?.index === 1) {
        result = 'first'
      }
      if (!backwards && !!relevantWindow.innerSearch && (relevantWindow.innerSearch.matchCount === relevantWindow.innerSearch.index + 2)) {
        result = 'last'
      }
    }
    setWindows(prev => {
      return prev.map(w => w.focused && w.innerSearch ? ({
        ...w, innerSearch: {
          ...w.innerSearch, index: backwards
            ? Math.max(w.innerSearch.index - 1, 0)
            : w.innerSearch.matchCount
              ? Math.min(w.innerSearch.index + 1, w.innerSearch.matchCount - 1)
              : w.innerSearch.index + 1
        }
      }) : w)
    });
    return result;
  }, [windows]);

  const onInnerDocumentSearch = useCallback((text: string, caseSensitive?: boolean, backwards?: boolean) => {
    const focusedWindow = windows.find(w => w.focused);
    if (!focusedWindow) return null;
    if (focusedWindow.innerSearch?.text === text) {
      return onNextInnerDocumentSearch(backwards);
    } else {
      setWindows(prev => prev.map(w => w.focused ? ({ ...w, innerSearch: { text, index: 0, caseSensitive } }) : w));
      return 'first';
    }

  }, [onNextInnerDocumentSearch, windows]);


  const onInnerDocumentSearchResult = useCallback((match: number) => {
    setWindows(prev => prev.map(w => w.focused && w.innerSearch ? ({ ...w, innerSearch: { ...w.innerSearch, matchCount: match } }) : w))
  }, []);

  const unsetInnerDocumentSearch = useCallback(() => {
    setWindows(prev => prev.map(w => ({ ...w, innerSearch: undefined })));
  }, []);

  const clearSearchProps = useCallback(() => {
    setSearchProps(emptySearchProps);
  }, []);

  const bringToFront = useCallback((windowId: string): void => {
    setWindows((prev) => {
      const maxZIndex = Math.max(...prev.map((w) => w.zIndex), 0);
      return prev.map((w) => (w.id === windowId ? { ...w, zIndex: maxZIndex + 1, focused: true } : { ...w, focused: false }));
    });
  }, []);

  const tileWindowsToGrid = useCallback((): void => {
    const windowContainer = document.getElementById("virtual-windows-container");
    const maxWindowWidth = windowContainer ? windowContainer.getBoundingClientRect().width : 1500;
    const maxWindowHeight = windowContainer ? windowContainer.getBoundingClientRect().height : 880;

    setWindows((prev) => {
      if (prev.length === 0) return [];

      const cols = Math.floor(Math.sqrt(prev.length));
      const windowCount = prev.length;
      const windowWidth = Math.floor(maxWindowWidth / cols);

      const perfectGridOverflow = windowCount - cols * cols;
      const colsWithExtraWindow = windowCount % cols;

      return prev.map((window, index) => {
        const currentColId = cols - (index % cols) - 1;
        const currentRowId = Math.floor(index / cols);

        const windowsPerCurrentCol =
          perfectGridOverflow > 0 ? Math.floor(windowCount / cols) + (cols - colsWithExtraWindow <= currentColId ? 1 : 0) : cols;

        const windowHeight = Math.floor(maxWindowHeight / windowsPerCurrentCol);

        return {
          ...window,
          isMaximized: false,
          position: {
            x: currentColId * windowWidth,
            y: currentRowId * windowHeight,
          },
          size: {
            width: windowWidth,
            height: windowHeight,
          },
          zIndex: 1,
        };
      });
    });
  }, []);

  const tileWindowsHorizontally = useCallback((): void => {
    const windowContainer = document.getElementById("virtual-windows-container");
    const maxWindowWidth = windowContainer ? windowContainer.getBoundingClientRect().width : 1500;
    const maxWindowHeight = windowContainer ? windowContainer.getBoundingClientRect().height : 880;

    setWindows((prev) => {
      if (prev.length === 0) return [];
      const windowCount = prev.length;
      const windowHeight = Math.floor(maxWindowHeight / windowCount); // Assuming container height of 800px

      return prev.map((window, index) => {
        // Reset any maximized windows
        const isMaximized = false;

        // Calculate tiled position
        const position = {
          x: 0,
          y: index * windowHeight,
        };

        // Set size for horizontal tiling
        const size = {
          width: maxWindowWidth, // Assuming container width of 800px
          height: windowHeight,
        };

        // Set z-index (all equal in tiled arrangement)
        const zIndex = 1;

        return {
          ...window,
          isMaximized,
          position,
          size,
          zIndex,
        };
      });
    });
  }, []);

  const openLatestDocsDocument = useCallback((): void => {
    setWindows((prev) => {
      if (prev.find((w) => w.docId === "noutati-legislative")) return prev;

      const maxZIndex = Math.max(...prev.map((w) => w.zIndex), 0);

      const newWindow: TVirtualWindowData = {
        id: uuidv4(),
        docId: "noutati-legislative",
        title: "Noutati legislative",
        isMaximized: false,
        position: getNextWindowStartPoint(prev),
        size: { width: 600, height: 400 },
        zIndex: maxZIndex + 1,
        focused: true,
        history: {
          docIds: ["noutati-legislative"],
          currentIndex: 0,
        },
      };
      return [...prev, newWindow];
    });
  }, []);

  const showFocusedWindowsDocumentRels = useCallback(async () => {
    const focusedWindow = windows.find((w) => w.focused);
    if (focusedWindow) {
      setLoadingDocumentRelsData(true);
      const doc = await newApiService.loadDocumentById(focusedWindow.docId);
      if (doc && doc.relations.length > 0) setShowDocumentRelsData(doc.relations);
      setLoadingDocumentRelsData(false);
    }
  }, [windows]);

  const showFocusedWindowsDocumentHistory = useCallback(async () => {
    const focusedWindow = windows.find((w) => w.focused);
    if (focusedWindow) {
      setLoadingDocumentHistoryData(true);
      const doc = await newApiService.loadDocumentById(focusedWindow.docId);
      if (doc && doc.revisions.length > 0) {
        setShowDocumentHistoryData({
          docid: doc.docid,
          lexid: doc.lexid,
          title: doc.title,
          revisions: doc.revisions,
        });
      }
      setLoadingDocumentHistoryData(false);
    }
  }, [windows]);

  const addFocusedWindowToFavorites = useCallback(async () => {
    const focusedWindow = windows.find((w) => w.focused);
    if (focusedWindow) {
      if (cache.isBookmarked(focusedWindow.docId)) {
        return false;
      } else {
        await newApiService.addToBookmarks(focusedWindow.docId);
        cache.invalidate(CACHE_KEYS.BOOKMARKS);
        // TODO = maybe wait for it to be saved and confirm? instead of trusting the backend does it?
        return true;
      }
    }
    return undefined;
  }, [cache, windows]);

  const toggleFocusedWindowFavorites = useCallback(async () => {
    const focusedWindow = windows.find((w) => w.focused);
    if (focusedWindow) {
      if (cache.isBookmarked(focusedWindow.docId)) {
        console.log("true", focusedWindow.docId, focusedWindow.title);
        await newApiService.removeFromBookmarks(focusedWindow.docId);
      } else {
        console.log("false", focusedWindow.docId, focusedWindow.title);
        await newApiService.addToBookmarks(focusedWindow.docId);
      }
      cache.invalidate(CACHE_KEYS.BOOKMARKS);
    }
  }, [cache, windows]);

  const toggleFocusedWindowReadLater = useCallback(() => {
    const focusedWindow = windows.find((w) => w.focused);
    if (focusedWindow) {
      if (cache.isReadLater(focusedWindow.docId)) {
        newApiService.removeReadLaterDoc(focusedWindow.docId);
      } else {
        newApiService.addReadLaterDoc(focusedWindow.docId);
      }
    }
  }, [cache, windows]);

  const openLatestDocsWindow = useCallback(() => {
    setWindows((prev) => {
      if (prev.find((w) => w.id === ULTIMELE_APARITII_WINDOW_ID)) {
        bringToFront(ULTIMELE_APARITII_WINDOW_ID);
        return prev;
      }

      const maxZIndex = Math.max(...prev.map((w) => w.zIndex), 0);
      const newWindow: TVirtualWindowData = {
        id: ULTIMELE_APARITII_WINDOW_ID,
        docId: ULTIMELE_APARITII_WINDOW_ID,
        title: "Ultimele aparitii",
        isMaximized: false,
        position: getNextWindowStartPoint(prev),
        size: { width: 600, height: 400 },
        zIndex: maxZIndex + 1,
        focused: true,
        customWindow: true,
        history: {
          docIds: [ULTIMELE_APARITII_WINDOW_ID],
          currentIndex: 0,
        },
      };
      return [...prev.map((w) => ({ ...w, focused: false })), newWindow];
    });
  }, [bringToFront]);

  // Open a new virtual window
  const openDocument = (doc: TDocumentBase): void => {
    setWindows((prev) => {
      const myWindow = prev.find((w) => w.docId === doc.id);
      if (myWindow) {
        bringToFront(myWindow.id);
        return prev;
      }

      const maxZIndex = Math.max(...prev.map((w) => w.zIndex), 0);

      const newWindow: TVirtualWindowData = {
        id: uuidv4(),
        docId: doc.id,
        title: doc.title,
        isMaximized: false,
        position: getNextWindowStartPoint(prev),
        size: { width: 600, height: 400 },
        zIndex: maxZIndex + 1,
        focused: true,
        history: {
          docIds: [doc.id],
          currentIndex: 0,
        },
      };
      return [...prev.map((w) => ({ ...w, focused: false })), newWindow];
    });
  };

  const updateWindowTitle = useCallback((windowId: string, title: string) => {
    setWindows((prev) => {
      return prev.map((window) =>
        window.id === windowId
          ? {
            ...window,
            title,
          }
          : window
      );
    });
  }, []);

  const openLink = useCallback((windowId: string, linkedDocId: string, anchorId?: string) => {
    setWindows((prev) => {
      const existing = prev.find((w) => w.docId === linkedDocId);
      if (existing) {
        bringToFront(existing.id);
        return prev.map((w) => w.id === existing.id ? { ...w, anchorId } : w);
      }
      const maxZIndex = Math.max(...prev.map((w) => w.zIndex), 0);
      const newWindow: TVirtualWindowData = {
        id: uuidv4(),
        docId: linkedDocId,
        anchorId,
        title: '',
        isMaximized: false,
        position: getNextWindowStartPoint(prev),
        size: { width: 600, height: 400 },
        zIndex: maxZIndex + 1,
        focused: true,
        history: {
          docIds: [linkedDocId],
          currentIndex: 0,
        },
      };
      return [...prev.map((w) => ({ ...w, focused: false })), newWindow];
    });
  }, [bringToFront]);

  const focusedWindowNext = () => {
    const focusedWindow = windows.find((w) => w.focused);
    if (!focusedWindow) return;
    setWindows((prev) =>
      prev.map((window) => {
        if (window.id !== focusedWindow.id) return window;

        // not if it's already at the end of the history
        if (window.history.docIds.length - 1 <= window.history.currentIndex) {
          return window;
        }
        return {
          ...window,
          docId: window.history.docIds[window.history.currentIndex + 1],
          history: {
            docIds: window.history.docIds,
            currentIndex: window.history.currentIndex + 1,
          },
        };
      })
    );
  };

  const focusedWindowPrevious = () => {
    const focusedWindow = windows.find((w) => w.focused);
    if (!focusedWindow) return;
    setWindows((prev) =>
      prev.map((window) => {
        if (window.id !== focusedWindow.id) return window;

        // not if it's already at the beginning of the history
        if (window.history.currentIndex === 0) {
          return window;
        }
        return {
          ...window,
          docId: window.history.docIds[window.history.currentIndex - 1],
          history: {
            docIds: window.history.docIds,
            currentIndex: window.history.currentIndex - 1,
          },
        };
      })
    );
  };

  // Close a window
  const closeWindow = (windowId: string): void => {
    setWindows((prev) => {
      const windowToClose = prev.find((w) => w.id === windowId);
      if (!windowToClose) return prev;
      const remainingWindows = prev.filter((w) => w.id !== windowId);
      if (!windowToClose.focused) return remainingWindows;
      if (remainingWindows.length === 0) return remainingWindows;

      remainingWindows.sort((a, b) => b.zIndex - a.zIndex);
      remainingWindows[0].focused = true;
      return remainingWindows;
    });
    setMinimizedWindows((prev) => prev.filter((w) => w.id !== windowId));
  };

  // Close focused window
  const closeFocusedWindow = (): void => {
    setWindows((prev) => prev.filter((w) => !w.focused));
  };

  // Minimize a window
  const minimizeWindow = (windowId: string): void => {
    // if I change this to use setWindows(prev => ...), it no longer works (it doubles all minimizedWindows)
    const windowToMinimize = windows.find((w) => w.id === windowId);
    if (windowToMinimize && !minimizedWindows.some((w) => w.id === windowId)) {
      setMinimizedWindows((prev) => [...prev, windowToMinimize]);
      setWindows((prev) => prev.filter((w) => w.id !== windowId));
    }
  };

  // Minimize focused window
  const minimizeFocusedWindow = (): void => {
    // if I change this to use setWindows(prev => ...), it no longer works (it doubles all minimizedWindows)
    const windowToMinimize = windows.find((w) => w.focused);
    if (windowToMinimize) {
      setMinimizedWindows((prev) => [...prev, windowToMinimize]);
      setWindows((prev) => prev.filter((w) => w.id !== windowToMinimize.id));
    }
  };

  const minimizeVisibleMaximizedWindow = (): void => {
    // multiple windows can be maximized - need to tackle the visible one
    const maximizedWindows = windows.filter((w) => w.isMaximized).sort((a, b) => a.zIndex - b.zIndex);
    if (maximizedWindows.length > 0) {
      const relevantWindow = maximizedWindows[0];
      setMinimizedWindows(prev => [...prev, relevantWindow]);
      setWindows(prev => prev.filter(w => w.id !== relevantWindow.id));
    }
  };

  const restoreVisibleMaximizedWindow = (): void => {
    // multiple windows can be maximized - need to tackle the visible one
    const maximizedWindows = windows.filter((w) => w.isMaximized).sort((a, b) => a.zIndex - b.zIndex);
    if (maximizedWindows.length > 0) {
      const relevantWindow = maximizedWindows[0];
      setWindows(prev => prev.map(w => w.id === relevantWindow.id ? { ...w, isMaximized: false } : w));
    }
  };

  const closeVisibleMaximizedWindow = (): void => {
    // multiple windows can be maximized - need to tackle the visible one
    const maximizedWindows = windows.filter((w) => w.isMaximized).sort((a, b) => a.zIndex - b.zIndex);
    if (maximizedWindows.length > 0) {
      const relevantWindow = maximizedWindows[0];
      setWindows(prev => prev.filter(w => w.id !== relevantWindow.id));
    }
  };

  // Maximize a window
  const maximizeWindow = (windowId: string): void => {
    setWindows((prev) => prev.map((w) => (w.id === windowId ? { ...w, isMaximized: true } : w)));
  };

  // Maximize focused window
  const maximizeRestoreFocusedWindow = (): void => {
    setWindows((prev) => prev.map((w) => (w.focused ? { ...w, isMaximized: !w.isMaximized } : w)));
  };

  // Restore a minimized window
  const restoreWindow = (windowId: string): void => {
    const windowToRestore = minimizedWindows.find((w) => w.id === windowId);
    if (windowToRestore) {
      setWindows((prev) => [...prev, windowToRestore]);
      setMinimizedWindows((prev) => prev.filter((w) => w.id !== windowId));
    }
  };

  // Update window position
  const updateWindowPosition = (windowId: string, position: TWindowPosition): void => {
    setWindows((prev) => prev.map((w) => (w.id === windowId ? { ...w, position } : w)));
  };

  const printFocusedWindow = useCallback((): void => {
    const focusedWindow = windows.find((w) => w.focused);
    if (focusedWindow) {
      printDiv(focusedWindow.id);
    }
  }, [windows]);

  const toggleShowReds = useCallback((windowId: string): void => {
    setWindows((prev) => prev.map((w) => (w.id === windowId ? { ...w, showReds: w.showReds === false } : w)));
  }, []);

  const exportFocusedWindowDocx = useCallback(async (): Promise<void> => {
    const focusedWindow = windows.find((w) => w.focused);
    if (!focusedWindow) return;
    const showReds = focusedWindow.showReds !== false;
    const html = getSelectionHtml();
    setExportingFormat('docx');
    try {
      const doc = await newApiService.loadDocumentById(focusedWindow.docId);
      if (!doc) return;
      const title = titleForDoc(doc, Boolean(html));
      const data = html
        ? await newApiService.docxgenForHTML(html)
        : await newApiService.docxgenForDoc(doc.docid, !showReds);
      if (!data) return;
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      (window as any).saveAs(blob, `Lex - ${title + (html ? ' (selectie)' : '')}.docx`);
    } finally {
      setExportingFormat(undefined);
    }
  }, [windows]);

  const exportFocusedWindowPDF = useCallback(async (): Promise<void> => {
    const focusedWindow = windows.find((w) => w.focused);
    if (!focusedWindow) return;
    const showReds = focusedWindow.showReds !== false;
    const html = getSelectionHtml();
    setExportingFormat('pdf');
    try {
      const doc = await newApiService.loadDocumentById(focusedWindow.docId);
      if (!doc) return;
      const title = titleForDoc(doc, Boolean(html));
      const data = html
        ? await newApiService.pdfgenForHTML(html)
        : await newApiService.pdfgenForDoc(doc.docid, !showReds);
      if (!data) return;
      const blob = new Blob([data], { type: 'application/pdf' });
      (window as any).saveAs(blob, `Lex - ${title + (html ? ' (selectie)' : '')}.pdf`);
    } finally {
      setExportingFormat(undefined);
    }
  }, [windows]);

  const updateWindowSize = (windowId: string, size: TWindowSize): void => {
    setWindows((prev) => prev.map((w) => (w.id === windowId ? { ...w, size } : w)));
  };

  // Cascade windows
  const cascadeWindows = (): void => {
    setWindows((prev) => {
      if (prev.length === 0) return [];
      return prev.map((window, index) => {
        // Reset any maximized windows
        const isMaximized = false;

        // Calculate cascade position
        const position = {
          x: 48 + index * 16,
          y: 48 + index * 16,
        };

        // Standard size for all windows
        const size = {
          width: 600,
          height: 400,
        };

        // Set z-index based on position in cascade
        const zIndex = index + 1;

        return {
          ...window,
          isMaximized,
          position,
          size,
          zIndex,
        };
      });
    });
  };

  // Tile windows vertically
  const tileWindowsVertically = (): void => {
    setWindows((prev) => {
      if (prev.length === 0) return [];
      const windowCount = prev.length;
      const windowWidth = Math.floor(800 / windowCount); // Assuming container width of 800px

      return prev.map((window, index) => {
        // Reset any maximized windows
        const isMaximized = false;

        // Calculate tiled position
        const position = {
          x: index * windowWidth,
          y: 0,
        };

        // Set size for vertical tiling
        const size = {
          width: windowWidth,
          height: 800, // Assuming container height of 800px
        };

        // Set z-index (all equal in tiled arrangement)
        const zIndex = 1;

        return {
          ...window,
          isMaximized,
          position,
          size,
          zIndex,
        };
      });
    });
  };

  // Minimize all windows
  const minimizeAllWindows = (): void => {
    // if I change this to use setWindows(prev => ...), it no longer works (it doubles all minimizedWindows)

    if (windows.length === 0) return;

    setMinimizedWindows([...minimizedWindows, ...windows]);
    setWindows([]);
  };

  // Close all windows
  const closeAllWindows = (): void => {
    setWindows([]);
  };

  const showAdvancedSearch = (): void => {
    setShowAdvancedSearchModal(true);
  };

  const showInDocumentSearch = (): void => {
    setShowInDocumentSearchModal(true);
  };

  const selectAllTextInFocusedWindow = useCallback(() => {
    const focusedWindow = windows.find((w) => w.focused);
    if (!focusedWindow) return;
    selectAllTextInDiv(focusedWindow.id);
  }, [windows]);

  const isFocusedWindowInFavorites = useMemo(() =>
    !!(windows.find((w) => w.focused) && cache.isBookmarked(windows.find((w) => w.focused)?.docId || ""))
    , [cache, windows]);

  const { menuItems }: { hasOpenDocs: boolean; menuItems: TMenuItem[] } = useMemo(() => {
    const hasOpenDocs = windows.length > 0;
    return {
      hasOpenDocs,
      menuItems: [
        {
          type: EMenuItemTypes.TEXT,
          name: EMenuItems.LAWS,
          label: "Legi",
          contents: [
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              label: "Cautare",
              name: "search",
              onClick: () => {
                setShowAdvancedSearchModal(true);
              },
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              label: "Ultimele aparitii",
              name: "latest",
              onClick: () => openLatestDocsWindow(),
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: hasOpenDocs,
              orientation: "column",
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Tiparire",
              name: "print",
              shortcut: {
                key: "P",
                isCTRL: true,
              },
              onClick: () => printFocusedWindow(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Tiparire selectie",
              name: "print-selection",
              shortcut: {
                key: "P",
                isCTRL: true,
                isShift: true,
              },
              onClick: () => handlePrintSelection(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Export Word",
              name: "export-docx",
              onClick: () => exportFocusedWindowDocx(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Export PDF",
              name: "export-pdf",
              onClick: () => exportFocusedWindowPDF(),
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: true,
              orientation: "column",
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              label: "Iesire",
              name: "exit",
              shortcut: {
                key: "F4",
                isALT: true,
              },
              onClick: () => window.close(),
            },
          ],
        },
        {
          type: EMenuItemTypes.TEXT,
          name: EMenuItems.TEXT,
          label: "Text",
          hideIfNoContents: true,
          contents: [
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Selecteaza tot",
              name: "select-all",
              shortcut: {
                isCTRL: true,
                key: "A",
              },
              onClick: () => selectAllTextInFocusedWindow(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Copiere",
              name: "copy",
              shortcut: {
                isCTRL: true,
                key: "C",
              },
              onClick: () => copyTextToClipboard(selectedText),
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: hasOpenDocs,
              orientation: "column",
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Cauta in text",
              name: "search-text",
              shortcut: {
                isCTRL: true,
                key: "F",
              },
              onClick: () => setShowInDocumentSearchModal(true),
            },
          ],
        },
        {
          type: EMenuItemTypes.TEXT,
          name: EMenuItems.VISUALIZE,
          label: "Vizualizare",
          contents: [
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              ticked: showDocumentControls,
              label: "Bara de comenzi",
              name: "toggle-view-document-controls",
              onClick: () => setShowDocumentControls((prev) => !prev),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              ticked: showStatusBar,
              label: "Bara de stare",
              name: "toggle-view-status-bar",
              onClick: () => setShowStatusBar((prev) => !prev),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              ticked: showDocumentControlsText,
              label: "Textul butoanelor",
              name: "toggle-view-document-controls-icons",
              onClick: () => setShowDocumentControlsText((prev) => !prev),
            },
          ],
        },
        {
          type: EMenuItemTypes.TEXT,
          name: EMenuItems.NAVIGATE,
          label: "Navigare",
          hideIfNoContents: true,
          contents: [
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Inapoi",
              name: "back",
              onClick: () => focusedWindowPrevious(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Inainte",
              name: "forward",
              onClick: () => focusedWindowNext(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Relatii",
              name: "links",
              onClick: () => showFocusedWindowsDocumentRels(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Istoric act",
              name: "history",
              onClick: () => showFocusedWindowsDocumentHistory(),
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: hasOpenDocs,
              orientation: "column",
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label:
                windows.find((w) => w.focused) && cache.isBookmarked(windows.find((w) => w.focused)?.docId || "")
                  ? "Scoate de la favorite"
                  : "Adauga la favorite",
              name: "bookmark",
              shortcut: {
                isCTRL: true,
                key: "D",
              },
              onClick: () => toggleFocusedWindowFavorites(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label:
                windows.find((w) => w.focused) && cache.isReadLater(windows.find((w) => w.focused)?.docId || "")
                  ? "Scoate de la acte de citit"
                  : "Adauga la acte de citit",
              name: "read-later",
              onClick: () => toggleFocusedWindowReadLater(),
            },
          ],
        },
        {
          type: EMenuItemTypes.TEXT,
          name: EMenuItems.WINDOWS,
          label: "Ferestre",
          hideIfNoContents: true,
          contents: [
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Cascada",
              name: "arrange-windows-as-cascade",
              onClick: () => cascadeWindows(),
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: hasOpenDocs,
              label: "Mozaic",
              name: "arrange-windows-as-grid",
              onClick: () => tileWindowsToGrid(),
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: hasOpenDocs,
              orientation: "column",
            },
            ...windows.map(
              (w) =>
              ({
                type: EMenuSubItemTypes.SUB_ITEM,
                visible: true,
                truncate: true,
                ticked: w.focused,
                label: w.title,
                name: w.docId,
                onClick: () => bringToFront(w.id),
              } as TMenuSubItem)
            ),
          ],
        },
        {
          type: EMenuItemTypes.TEXT,
          name: EMenuItems.HELP,
          label: "Ajutor",
          contents: [
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              label: "Manual de utilizare",
              name: "user-guide",
              shortcut: {
                key: "F1",
              },
              onClick: () => { },
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: true,
              orientation: "column",
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              ticked: theme === Themes.VINTAGE,
              label: `Tema vintage`,
              name: "vintage-theme",
              onClick: () => {
                if (theme !== Themes.VINTAGE) {
                  newApiService.updateUserTheme(Themes.VINTAGE).then(() => window.location.reload());
                }
              },
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              ticked: theme === Themes.MODERN,
              label: `Tema moderna`,
              name: "modern-theme",
              onClick: () => {
                if (theme !== Themes.MODERN) {
                  newApiService.updateUserTheme(Themes.MODERN).then(() => window.location.reload());
                }
              },
            },
            {
              type: EMenuSubItemTypes.SPACER,
              visible: true,
              orientation: "column",
            },
            {
              type: EMenuSubItemTypes.SUB_ITEM,
              visible: true,
              label: "Despre Lex",
              name: "about",
              onClick: () => { },
            },
          ],
        },
      ],
    };
  }, [
    windows,
    showDocumentControls,
    showStatusBar,
    showDocumentControlsText,
    cache,
    theme,
    openLatestDocsWindow,
    printFocusedWindow,
    exportFocusedWindowDocx,
    exportFocusedWindowPDF,
    selectAllTextInFocusedWindow,
    selectedText,
    showFocusedWindowsDocumentRels,
    showFocusedWindowsDocumentHistory,
    toggleFocusedWindowFavorites,
    toggleFocusedWindowReadLater,
    tileWindowsToGrid,
    bringToFront,
  ]);

  // Fetch "ultimele-aparitii" on component mount
  useEffect(() => {
    openLatestDocsWindow();
  }, [openLatestDocsWindow]);

  useEffect(() => {
    const handleShortcuts = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.code === "KeyC") {
        event.preventDefault();
        copyTextToClipboard(selectedText);
      }
    };

    window.addEventListener("keydown", handleShortcuts);
    return () => window.removeEventListener("keydown", handleShortcuts);
  }, [selectedText]);

  useEffect(() => {
    const handlePrintShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.code === "KeyA") {
        event.preventDefault();
        const focusedWindow = windows.find((w) => w.focused);
        if (focusedWindow) selectAllTextInDiv(focusedWindow.id);
      } else if ((event.ctrlKey || event.metaKey) && event.code === "KeyP") {
        event.preventDefault();
        const focusedWindow = windows.find((w) => w.focused);
        if (focusedWindow) printDiv(focusedWindow.id);
      } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.code === "KeyP") {
        event.preventDefault();
        handlePrintSelection();
      }
    };

    window.addEventListener("keydown", handlePrintShortcut);
    return () => window.removeEventListener("keydown", handlePrintShortcut);
  }, [windows]);

  return (
    <DesktopContext.Provider
      value={{
        cache,
        showDocumentControls,
        showStatusBar,
        showDocumentControlsText,
        menuItems,
        searchResults,
        isLoading,
        error,
        windows,
        minimizedWindows,
        activeTab,
        setActiveTab,
        searchProps,
        setSearchProps,
        clearSearchProps,
        onSearch,
        searchWithFilters,
        onInnerDocumentSearch,
        onNextInnerDocumentSearch,
        onInnerDocumentSearchResult,
        unsetInnerDocumentSearch,
        openDocument,
        openLatestDocsDocument,
        openLink,
        focusedWindowPrevious,
        focusedWindowNext,
        updateWindowTitle,
        closeWindow,
        closeFocusedWindow,
        minimizeWindow,
        minimizeFocusedWindow,
        maximizeWindow,
        maximizeRestoreFocusedWindow,
        minimizeVisibleMaximizedWindow,
        restoreVisibleMaximizedWindow,
        closeVisibleMaximizedWindow,
        restoreWindow,
        printFocusedWindow,
        printSelection: handlePrintSelection,
        toggleShowReds,
        exportFocusedWindowDocx,
        exportFocusedWindowPDF,
        bringToFront,
        updateWindowPosition,
        cascadeWindows,
        tileWindowsHorizontally,
        tileWindowsVertically,
        minimizeAllWindows,
        closeAllWindows,
        updateWindowSize,
        showAdvancedSearch,
        showInDocumentSearch,
        showFocusedWindowsDocumentRels,
        showFocusedWindowsDocumentHistory,
        addFocusedWindowToFavorites,
        toggleFocusedWindowFavorites,
        hasFocusedWindow,
        hasAnyFocusedWindow,
        isFocusedWindowInFavorites,
      }}
    >
      {children}
      {showAdvancedSearchModal && theme ? <AdvancedSearchVintageFullModal onClose={() => setShowAdvancedSearchModal(false)} /> : null}
      {showInDocumentSearchModal
        ? <InDocumentSearchModal
          theme={theme}
          onClose={() => setShowInDocumentSearchModal(false)}
        />
        : null}
      {
        (showDocumentRelsData || loadingDocumentRelsData) && theme
          ? <DocumentRelsModal
            data={showDocumentRelsData || []}
            loading={loadingDocumentRelsData}
            theme={theme}
            onClose={() => setShowDocumentRelsData(undefined)}
          />
          : null
      }
      {
        (showDocumentHistoryData || loadingDocumentHistoryData) && theme
          ? <DocumentHistoryModal
            data={showDocumentHistoryData}
            loading={loadingDocumentHistoryData}
            theme={theme}
            onClose={() => setShowDocumentHistoryData(undefined)}
          />
          : null
      }
      {showNoSelectionModal && theme
        ? <NoSelectionModal theme={theme} onClose={() => setShowNoSelectionModal(false)} />
        : null}
      {exportingFormat && theme
        ? <ExportingModal theme={theme} format={exportingFormat} />
        : null}
    </DesktopContext.Provider>
  );
};

export const useDocuments = (): TDesktopContext => {
  const context = useContext(DesktopContext);
  if (context === undefined) {
    throw new Error("useDocuments must be used within a DocumentProvider");
  }
  return context;
};
